import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  products as productsApi,
  tiers as tiersApi,
  features as featuresApi,
  entitlements as entApi,
  type Product,
  type Tier,
  type Feature,
  type EntitlementsMatrix,
} from '../api';
import { formatMoney, featureKeySafe } from '../lib/format';
import { useToast } from '../lib/toast';
import { Modal, Field, inputCls } from '../lib/Modal';

// Debounce per (tier_id|feature_id)
const limitTimers: Record<string, ReturnType<typeof setTimeout>> = {};

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [showAddProduct, setShowAddProduct] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await productsApi.list();
      setProducts(list);
      // auto-expand first one
      if (list.length && Object.keys(expanded).length === 0) {
        setExpanded({ [list[0].id]: true });
      }
    } catch (e) { setError((e as Error).message); }
  }, [expanded]);

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
        <h1 className="text-xl sm:text-2xl font-semibold">Products</h1>
        <button onClick={() => setShowAddProduct(true)}
                className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-3 sm:px-4 py-2 rounded transition-colors whitespace-nowrap">
          + Add Product
        </button>
      </div>

      {error && (
        <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">
          Failed to load products: {error}
        </div>
      )}
      {products === null && !error && (
        <div className="text-ink-muted text-sm">Loading products…</div>
      )}
      {products && products.length === 0 && (
        <div className="bg-ink-card border border-ink-border rounded p-8 text-center text-ink-muted text-sm">
          No products yet. Create one to define tiers, features and entitlements.
        </div>
      )}

      {products && products.map(p => (
        <ProductCard
          key={p.id}
          product={p}
          expanded={!!expanded[p.id]}
          onToggle={() => setExpanded(e => ({ ...e, [p.id]: !e[p.id] }))}
          onReload={load}
        />
      ))}

      <AddProductModal open={showAddProduct} onClose={() => setShowAddProduct(false)} onSaved={load} />
    </div>
  );
}

function ProductCard({
  product, expanded, onToggle, onReload,
}: {
  product: Product;
  expanded: boolean;
  onToggle: () => void;
  onReload: () => void;
}) {
  return (
    <div className="bg-ink-card border border-ink-border rounded-lg mb-4">
      <button onClick={onToggle}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-ink-hover/50 transition-colors">
        <div className="flex items-center gap-3">
          <span className="text-base font-semibold">{product.name}</span>
          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
            product.status === 'active' ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'
          }`}>{product.status}</span>
        </div>
        <span className={`text-ink-muted transition-transform ${expanded ? 'rotate-180' : ''}`}>⌄</span>
      </button>
      {expanded && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-ink-border pt-4">
          <ProductMeta product={product} onReload={onReload} />
          <TiersTable product={product} onReload={onReload} />
          <EntitlementsSection productId={product.id} />
        </div>
      )}
    </div>
  );
}

function ProductMeta({ product, onReload }: { product: Product; onReload: () => void }) {
  const { push } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: product.name, slug: product.slug,
    description: product.description || '', domain: product.domain || '',
    status: product.status,
  });

  async function save() {
    try {
      await productsApi.update(product.id, draft);
      push('Product updated', 'success');
      setEditing(false);
      onReload();
    } catch (e) { push('Save failed: ' + (e as Error).message, 'error'); }
  }

  if (!editing) {
    return (
      <div className="mb-5 text-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          <div><span className="text-ink-muted">Slug:</span> <span className="font-mono text-xs">{product.slug}</span></div>
          <div><span className="text-ink-muted">Domain:</span> {product.domain || '—'}</div>
          <div><span className="text-ink-muted">Active subscribers:</span> {product.active_subscribers}</div>
          <div><span className="text-ink-muted">Status:</span> {product.status}</div>
          <div className="col-span-2"><span className="text-ink-muted">Description:</span> {product.description || '—'}</div>
        </div>
        <button onClick={() => setEditing(true)} className="text-xs text-brand-400 hover:text-brand-300 mt-2">
          Edit product
        </button>
      </div>
    );
  }

  return (
    <div className="bg-ink-bg border border-ink-border rounded p-4 mb-5">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name"><input className={inputCls} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></Field>
        <Field label="Slug"><input className={inputCls} value={draft.slug} onChange={e => setDraft({ ...draft, slug: e.target.value })} /></Field>
        <Field label="Domain"><input className={inputCls} value={draft.domain} onChange={e => setDraft({ ...draft, domain: e.target.value })} /></Field>
        <Field label="Status">
          <select className={inputCls} value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })}>
            <option value="active">active</option><option value="inactive">inactive</option>
          </select>
        </Field>
        <div className="col-span-2">
          <Field label="Description">
            <textarea className={inputCls + ' min-h-[80px] resize-y'} value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} />
          </Field>
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <button onClick={save} className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded">Save</button>
        <button onClick={() => setEditing(false)} className="bg-ink-border hover:bg-ink-border/70 text-ink-text text-sm px-4 py-2 rounded">Cancel</button>
      </div>
    </div>
  );
}

function TiersTable({ product, onReload }: { product: Product; onReload: () => void }) {
  const { push } = useToast();
  const [editingTier, setEditingTier] = useState<Tier | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const tiers = useMemo(() => [...(product.tiers || [])].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)), [product.tiers]);

  async function archiveTier(t: Tier) {
    if (!confirm(`Archive tier "${t.name}"? Existing subscribers retain access; new sign-ups can't pick it.`)) return;
    try {
      await tiersApi.archive(t.id);
      push('Tier archived', 'success');
      onReload();
    } catch (e) { push('Archive failed: ' + (e as Error).message, 'error'); }
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-muted">Tiers</h3>
        <button onClick={() => setShowAdd(true)}
                className="text-xs bg-brand-600 hover:bg-brand-700 text-white font-semibold px-3 py-1.5 rounded">
          + Add Tier
        </button>
      </div>
      <div className="bg-ink-bg border border-ink-border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-card border-b border-ink-border text-xs uppercase text-ink-muted">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Tier</th>
              <th className="text-right px-4 py-2 font-semibold">Price / month</th>
              <th className="text-right px-4 py-2 font-semibold">Price / year</th>
              <th className="text-left px-4 py-2 font-semibold">Slug</th>
              <th className="text-right px-4 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map(t => (
              <tr key={t.id} className="border-b border-ink-border last:border-0">
                <td className="px-4 py-2 font-medium">{t.name}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatMoney(t.price_monthly)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{t.price_yearly ? formatMoney(t.price_yearly) : '—'}</td>
                <td className="px-4 py-2 font-mono text-xs text-ink-muted">{t.slug}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setEditingTier(t)}
                          className="text-xs bg-ink-border hover:bg-ink-border/70 text-ink-text px-2.5 py-1 rounded mr-1">Edit</button>
                  <button onClick={() => archiveTier(t)}
                          className="text-xs bg-state-danger/80 hover:bg-state-danger text-white px-2.5 py-1 rounded">Archive</button>
                </td>
              </tr>
            ))}
            {tiers.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-4 text-center text-ink-muted">No tiers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editingTier && (
        <TierEditModal
          tier={editingTier}
          onClose={() => setEditingTier(null)}
          onSaved={() => { setEditingTier(null); onReload(); }}
        />
      )}
      {showAdd && (
        <TierEditModal
          productId={product.id}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); onReload(); }}
        />
      )}
    </div>
  );
}

function TierEditModal({
  tier, productId, onClose, onSaved,
}: {
  tier?: Tier; productId?: number; onClose: () => void; onSaved: () => void;
}) {
  const { push } = useToast();
  const [name, setName] = useState(tier?.name || '');
  const [slug, setSlug] = useState(tier?.slug || '');
  const [priceMonthlyGBP, setPriceMonthlyGBP] = useState(
    tier ? (tier.price_monthly / 100).toString() : '0'
  );
  const [priceYearlyGBP, setPriceYearlyGBP] = useState(
    tier?.price_yearly ? (tier.price_yearly / 100).toString() : ''
  );
  const [displayOrder, setDisplayOrder] = useState((tier?.display_order ?? 0).toString());
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const body: Partial<Tier> & { product_id?: number } = {
        name: name.trim(),
        slug: slug.trim() || featureKeySafe(name),
        price_monthly: Math.round(parseFloat(priceMonthlyGBP || '0') * 100),
        price_yearly: priceYearlyGBP ? Math.round(parseFloat(priceYearlyGBP) * 100) : null,
        display_order: parseInt(displayOrder) || 0,
      };
      if (tier) {
        await tiersApi.update(tier.id, body);
        push('Tier updated', 'success');
      } else {
        await tiersApi.create({ ...body, product_id: productId! } as Parameters<typeof tiersApi.create>[0]);
        push('Tier added', 'success');
      }
      onSaved();
    } catch (e) { push('Save failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={true} title={tier ? 'Edit Tier' : 'Add Tier'} onClose={onClose}>
      <form onSubmit={save}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><input required className={inputCls} value={name} onChange={e => setName(e.target.value)} /></Field>
          <Field label="Slug"><input className={inputCls} value={slug} onChange={e => setSlug(e.target.value)} placeholder="auto from name" /></Field>
          <Field label="Price / month" hint="GBP"><input required type="number" min="0" step="0.01" className={inputCls} value={priceMonthlyGBP} onChange={e => setPriceMonthlyGBP(e.target.value)} /></Field>
          <Field label="Price / year" hint="GBP, optional"><input type="number" min="0" step="0.01" className={inputCls} value={priceYearlyGBP} onChange={e => setPriceYearlyGBP(e.target.value)} /></Field>
          <Field label="Display order"><input type="number" className={inputCls} value={displayOrder} onChange={e => setDisplayOrder(e.target.value)} /></Field>
        </div>
        <div className="flex gap-2 mt-4">
          <button type="submit" disabled={busy} className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded">
            {busy ? 'Saving…' : tier ? 'Save' : 'Add Tier'}
          </button>
          <button type="button" onClick={onClose} className="bg-ink-border hover:bg-ink-border/70 text-ink-text text-sm px-4 py-2 rounded">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Entitlements Matrix ───────────────────────────────────────────────────

function EntitlementsSection({ productId }: { productId: number }) {
  const { push } = useToast();
  const [matrix, setMatrix] = useState<EntitlementsMatrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editingFeat, setEditingFeat] = useState<Feature | null>(null);

  const load = useCallback(async () => {
    try { setMatrix(await entApi.matrix(productId)); setError(null); }
    catch (e) { setError((e as Error).message); }
  }, [productId]);

  useEffect(() => { void load(); }, [load]);

  async function toggle(tierId: number, featureId: number, enabled: boolean, limit_value: number | null) {
    try {
      await entApi.bulkSetTier([{ tier_id: tierId, feature_id: featureId, enabled, limit_value }]);
      // optimistic update
      setMatrix(m => {
        if (!m) return m;
        const next = { ...m, entitlements: { ...m.entitlements } };
        next.entitlements[tierId] = { ...(next.entitlements[tierId] || {}) };
        next.entitlements[tierId][featureId] = { enabled, limit_value };
        return next;
      });
    } catch (e) { push('Toggle failed: ' + (e as Error).message, 'error'); }
  }

  function onLimitChange(tierId: number, featureId: number, raw: string) {
    const key = `${tierId}_${featureId}`;
    if (limitTimers[key]) clearTimeout(limitTimers[key]);
    limitTimers[key] = setTimeout(async () => {
      const lv = parseInt(raw) || 0;
      const enabled = lv > 0;
      try {
        await entApi.bulkSetTier([{ tier_id: tierId, feature_id: featureId, enabled, limit_value: lv }]);
        push('Limit saved', 'success');
        setMatrix(m => {
          if (!m) return m;
          const next = { ...m, entitlements: { ...m.entitlements } };
          next.entitlements[tierId] = { ...(next.entitlements[tierId] || {}) };
          next.entitlements[tierId][featureId] = { enabled, limit_value: lv };
          return next;
        });
      } catch (e) { push('Save failed: ' + (e as Error).message, 'error'); }
    }, 600);
  }

  async function deleteFeature(f: Feature) {
    if (!confirm(`Delete feature "${f.feature_name}"? This removes all tier entitlements for it.`)) return;
    try {
      await featuresApi.remove(f.id);
      push('Feature deleted', 'success');
      void load();
    } catch (e) { push('Delete failed: ' + (e as Error).message, 'error'); }
  }

  if (error) return <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm">Failed to load matrix: {error}</div>;
  if (!matrix) return <div className="text-sm text-ink-muted">Loading entitlements matrix…</div>;

  const tiers = [...matrix.tiers].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  return (
    <div className="border-t border-ink-border pt-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-muted">Feature Entitlements Matrix</h3>
        <div className="flex gap-2">
          <button onClick={() => setAddOpen(true)}
                  className="text-xs bg-brand-600 hover:bg-brand-700 text-white font-semibold px-3 py-1.5 rounded">+ Add Feature</button>
          <button onClick={load}
                  className="text-xs bg-ink-border hover:bg-ink-border/70 text-ink-text px-3 py-1.5 rounded">Refresh</button>
        </div>
      </div>

      {matrix.features.length === 0 ? (
        <div className="bg-ink-bg border border-ink-border rounded p-6 text-center text-ink-muted text-sm">
          No features defined. Add one above to begin.
        </div>
      ) : (
        <div className="bg-ink-bg border border-ink-border rounded overflow-x-auto relative">
          <p className="lg:hidden text-[10px] text-ink-muted px-3 pt-2">← Swipe to see all tiers →</p>
          <table className="w-full text-sm" style={{ minWidth: `${220 + (tiers.length * 130) + 100}px` }}>
            <thead className="bg-ink-card border-b border-ink-border text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left px-3 sm:px-4 py-2 font-semibold sticky left-0 bg-ink-card z-10 min-w-[160px] sm:min-w-[220px]">Feature</th>
                {tiers.map(t => (
                  <th key={t.id} className="text-center px-3 sm:px-4 py-2 font-semibold min-w-[110px] sm:min-w-[130px]">{t.name}</th>
                ))}
                <th className="text-center px-3 sm:px-4 py-2 font-semibold w-20 sm:w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {matrix.features.map(f => (
                <tr key={f.id} className="border-b border-ink-border last:border-0 hover:bg-ink-hover/30">
                  <td className="px-3 sm:px-4 py-3 sticky left-0 bg-ink-bg z-10">
                    <div className="font-medium">{f.feature_name}</div>
                    <div className="text-[11px] text-ink-muted font-mono truncate">{f.feature_key} ({f.feature_type})</div>
                  </td>
                  {tiers.map(t => {
                    const cell = matrix.entitlements[t.id]?.[f.id] || { enabled: false, limit_value: null };
                    return (
                      <td key={t.id} className="px-4 py-3 text-center">
                        {f.feature_type === 'limit' ? (
                          <div className="flex flex-col items-center gap-1.5">
                            <label className="toggle-switch">
                              <input type="checkbox" checked={cell.enabled}
                                     onChange={e => toggle(t.id, f.id, e.target.checked, e.target.checked ? (cell.limit_value ?? 0) : null)} />
                              <span className="toggle-slider" />
                            </label>
                            <input
                              type="number" min="0"
                              className="w-20 px-1.5 py-1 bg-ink-card border border-ink-border text-ink-text rounded text-xs text-center"
                              defaultValue={cell.limit_value ?? 0}
                              onChange={e => onLimitChange(t.id, f.id, e.target.value)}
                              title="Use 999999 for unlimited"
                            />
                          </div>
                        ) : (
                          <label className="toggle-switch">
                            <input type="checkbox" checked={cell.enabled}
                                   onChange={e => toggle(t.id, f.id, e.target.checked, null)} />
                            <span className="toggle-slider" />
                          </label>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <button onClick={() => setEditingFeat(f)}
                            className="text-xs bg-ink-border hover:bg-ink-border/70 text-ink-text px-2 py-1 rounded mr-1"
                            title="Edit feature">✎</button>
                    <button onClick={() => deleteFeature(f)}
                            className="text-xs bg-state-danger/80 hover:bg-state-danger text-white px-2 py-1 rounded"
                            title="Delete feature">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <FeatureModal productId={productId}
                      onClose={() => setAddOpen(false)}
                      onSaved={() => { setAddOpen(false); void load(); }} />
      )}
      {editingFeat && (
        <FeatureModal feature={editingFeat}
                      onClose={() => setEditingFeat(null)}
                      onSaved={() => { setEditingFeat(null); void load(); }} />
      )}
    </div>
  );
}

function FeatureModal({
  feature, productId, onClose, onSaved,
}: {
  feature?: Feature; productId?: number; onClose: () => void; onSaved: () => void;
}) {
  const { push } = useToast();
  const [key, setKey] = useState(feature?.feature_key || '');
  const [name, setName] = useState(feature?.feature_name || '');
  const [description, setDescription] = useState(feature?.feature_description || '');
  const [type, setType] = useState<'boolean' | 'limit'>((feature?.feature_type as 'boolean' | 'limit') || 'boolean');
  const [order, setOrder] = useState((feature?.display_order ?? 0).toString());
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        feature_key: featureKeySafe(key),
        feature_name: name.trim(),
        feature_description: description.trim(),
        feature_type: type,
        display_order: parseInt(order) || 0,
      };
      if (feature) {
        await featuresApi.update(feature.id, body);
        push('Feature updated', 'success');
      } else {
        await featuresApi.create(productId!, body);
        push('Feature added', 'success');
      }
      onSaved();
    } catch (e) { push('Save failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={true} title={feature ? 'Edit Feature' : 'Add Feature'} onClose={onClose}>
      <form onSubmit={save}>
        <Field label="Feature Key" hint="e.g. jobs, invoicing">
          <input required className={inputCls + ' font-mono'} value={key} onChange={e => setKey(e.target.value)} />
        </Field>
        <Field label="Feature Name">
          <input required className={inputCls} value={name} onChange={e => setName(e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea className={inputCls + ' min-h-[70px] resize-y'} value={description} onChange={e => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select className={inputCls} value={type} onChange={e => setType(e.target.value as 'boolean' | 'limit')}>
              <option value="boolean">boolean (on/off)</option>
              <option value="limit">limit (numeric)</option>
            </select>
          </Field>
          <Field label="Display order">
            <input type="number" className={inputCls} value={order} onChange={e => setOrder(e.target.value)} />
          </Field>
        </div>
        <div className="flex gap-2 mt-4">
          <button type="submit" disabled={busy} className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded">
            {busy ? 'Saving…' : feature ? 'Save' : 'Add Feature'}
          </button>
          <button type="button" onClick={onClose} className="bg-ink-border hover:bg-ink-border/70 text-ink-text text-sm px-4 py-2 rounded">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function AddProductModal({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const { push } = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await productsApi.create({
        name: name.trim(),
        slug: (slug.trim() || featureKeySafe(name)).replace(/_/g, '-'),
        domain: domain.trim(),
        description: description.trim(),
      });
      push('Product added', 'success');
      setName(''); setSlug(''); setDomain(''); setDescription('');
      onSaved();
    } catch (e) { push('Create failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={open} title="Add Product" onClose={onClose}>
      <form onSubmit={save}>
        <Field label="Name"><input required className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. OnlyTrade.ai" /></Field>
        <Field label="Slug"><input className={inputCls} value={slug} onChange={e => setSlug(e.target.value)} placeholder="auto from name" /></Field>
        <Field label="Domain"><input className={inputCls} value={domain} onChange={e => setDomain(e.target.value)} placeholder="onlytrade.ai" /></Field>
        <Field label="Description"><textarea className={inputCls + ' min-h-[70px] resize-y'} value={description} onChange={e => setDescription(e.target.value)} /></Field>
        <div className="flex gap-2 mt-4">
          <button type="submit" disabled={busy} className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded">
            {busy ? 'Creating…' : 'Add Product'}
          </button>
          <button type="button" onClick={onClose} className="bg-ink-border hover:bg-ink-border/70 text-ink-text text-sm px-4 py-2 rounded">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}
